<template>
  <b-modal
    v-model="model"
    size="3xl"
    :data-e2e="dataE2e"
  >
    <template #header>
      <!-- Empty span when header is hidden to prevent BModal's default header -->
      <span v-if="hideHeader" />
      <div v-else class="px-10 pt-10 pb-2">
        <div class="mb-5">
          <h1 class="mb-2">{{ title }}</h1>
          <p v-if="description" class="text-white font-semibold mb-2 text-lg">{{ description }}</p>
        </div>
        <b-stepper
          v-if="showStepper"
          :steps="totalVisibleSteps"
          :current-step="currentStep"
          v-model:current-step="stepperStep"
          :can-go-next="canGoNext"
          data-e2e="wizard-stepper"
        />
      </div>
    </template>

    <template #default>
      <div ref="contentRef" :class="contentClasses">
        <slot name="steps" :current-step="currentStep" />
      </div>
    </template>

    <template #footer>
      <!-- Empty span when footer is hidden to prevent BModal's default footer -->
      <span v-if="hideFooter" />
      <div v-else class="px-10 pb-10 pt-2 flex gap-4">
        <!-- Custom footer slot for special layouts (e.g., step 4 dual buttons) -->
        <slot name="footer" :current-step="currentStep" :go-back="goBack" :go-next="onNext" :loading="loading">
          <!-- Default footer layout -->
          <b-button
            v-if="showBackButton"
            light
            @click="goBack"
            data-e2e="wizard-back-button"
          >
            {{ backButtonText }}
          </b-button>

          <div class="flex-1" />

          <b-button
            :loading="loading"
            :disabled="!canGoNext"
            block
            @click="onNext"
            data-e2e="wizard-next-button"
          >
            {{ nextButtonText }}
          </b-button>
        </slot>
      </div>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import BModal from '~/components/modal/BModal.vue';
import BStepper from '~/components/stepper/BStepper.vue';
import BButton from '~/components/forms/BButton.vue';

interface Props {
  /** Total number of steps in the wizard */
  totalSteps: number;
  /** Number of steps visible in the stepper (may differ from totalSteps) */
  totalVisibleSteps?: number;
  /** Current step index (0-based) */
  currentStep: number;
  /** Title displayed in the header */
  title: string;
  /** Description displayed below the title */
  description?: string;
  /** Whether the current step data is valid */
  canGoNext?: boolean;
  /** Loading state for the next button */
  loading?: boolean;
  /** Text for the next/submit button */
  nextButtonText?: string;
  /** Text for the back button */
  backButtonText?: string;
  /** Steps where stepper should be shown (array of step indices) */
  stepperVisibleSteps?: number[];
  /** Steps where header should be hidden */
  hideHeaderSteps?: number[];
  /** Steps where footer should be hidden */
  hideFooterSteps?: number[];
  /** Steps where back button should be shown */
  showBackButtonSteps?: number[];
  /** Custom content classes per step */
  contentClassesPerStep?: Record<number, string>;
  /** E2E test identifier */
  dataE2e?: string;
}

const props = withDefaults(defineProps<Props>(), {
  totalVisibleSteps: 3,
  canGoNext: true,
  loading: false,
  nextButtonText: 'Suivant',
  backButtonText: 'Retour',
  stepperVisibleSteps: () => [0, 1, 2],
  hideHeaderSteps: () => [],
  hideFooterSteps: () => [],
  showBackButtonSteps: () => [1, 2],
  contentClassesPerStep: () => ({}),
  dataE2e: 'mission-wizard-modal',
});

const model = defineModel<boolean>({ default: false });
const emit = defineEmits<{
  'update:currentStep': [step: number];
  'next': [currentStep: number];
  'back': [currentStep: number];
  'stepper-navigate': [newStep: number];
}>();

// Ref on the content wrapper so we can reset the modal body scroll on step change.
// Steps are toggled via v-show (all mounted), so the shared scroll container keeps
// its scrollTop when switching steps — causing the next step to appear mid-page.
const contentRef = ref<HTMLElement | null>(null);

// Walk up from the content wrapper to the nearest scrollable ancestor (BModal's
// `overflow-y-auto` body) and reset it to the top.
const scrollContentToTop = () => {
  let el: HTMLElement | null = contentRef.value?.parentElement ?? null;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      el.scrollTop = 0;
      return;
    }
    el = el.parentElement;
  }
};

// On every step change (next, back or stepper navigation), land at the top of the step.
watch(
  () => props.currentStep,
  () => {
    nextTick(scrollContentToTop);
  },
);

// Computed properties for conditional rendering
const showStepper = computed(() => props.stepperVisibleSteps.includes(props.currentStep));
const hideHeader = computed(() => props.hideHeaderSteps.includes(props.currentStep));
const hideFooter = computed(() => props.hideFooterSteps.includes(props.currentStep));
const showBackButton = computed(() => props.showBackButtonSteps.includes(props.currentStep));

const contentClasses = computed(() => {
  const customClasses = props.contentClassesPerStep[props.currentStep];
  if (customClasses) return customClasses;
  return 'px-10 py-5';
});

// Stepper navigation (allows clicking on previous steps)
const stepperStep = computed({
  get: () => props.currentStep,
  set: (newValue: number) => {
    if (newValue < props.currentStep) {
      // Allow going back freely via stepper
      emit('update:currentStep', newValue);
    } else if (newValue > props.currentStep) {
      // Forward navigation via stepper triggers validation
      emit('stepper-navigate', newValue);
    }
  },
});

// Navigation methods
const goBack = () => {
  if (props.currentStep > 0) {
    emit('update:currentStep', props.currentStep - 1);
    emit('back', props.currentStep);
  }
};

const onNext = () => {
  emit('next', props.currentStep);
};
</script>
